import { useMemo, useState } from "react";
import { Search, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Skeleton } from "../components/ui/skeleton";
import {
  fmtPrice,
  fmtPct,
  fmtSignedNum,
  fmtWeight,
  pickTrendColor,
} from "../lib/format";

export function TickerTable({ tickers, loading, error, onSelect, selectedSymbol }) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!tickers) return [];
    const q = query.trim().toUpperCase();
    if (!q) return tickers;
    return tickers.filter(
      (t) =>
        t.symbol.toUpperCase().includes(q) ||
        (t.company || "").toUpperCase().includes(q),
    );
  }, [tickers, query]);

  return (
    <section
      data-testid="ticker-panel"
      className="border border-soft bg-surface flex flex-col h-full"
    >
      {/* --- Toolbar -------------------------------------------------- */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-soft">
        <div className="relative flex-1 max-w-md">
          <Search
            size={14}
            strokeWidth={1.5}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-soft pointer-events-none"
          />
          <Input
            data-testid="search-input"
            placeholder="FILTER SYMBOL OR COMPANY…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="rounded-none border-soft bg-[var(--bg)] font-mono-tight uppercase tracking-widest text-xs pl-9 h-9 placeholder:text-muted-soft focus-visible:ring-1 focus-visible:ring-[var(--accent-hex)]"
          />
        </div>
        <div className="ml-auto flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-muted-soft">
          <span data-testid="result-count">
            <span className="num text-[var(--text-primary)]">
              {loading ? "—" : filtered.length.toString().padStart(3, "0")}
            </span>
            <span className="mx-1">/</span>
            <span className="num">
              {loading ? "—" : (tickers?.length || 0).toString().padStart(3, "0")}
            </span>{" "}
            rows
          </span>
        </div>
      </div>

      {/* --- Table ---------------------------------------------------- */}
      <div className="flex-1 overflow-auto" data-testid="ticker-table-scroll">
        {error ? (
          <ErrorBlock error={error} />
        ) : (
          <Table className="border-separate border-spacing-0">
            <TableHeader className="sticky top-0 z-10 bg-surface">
              <TableRow className="border-b-2 border-soft hover:bg-transparent">
                <Th className="w-12 text-right">#</Th>
                <Th className="w-24">Symbol</Th>
                <Th>Company</Th>
                <Th className="w-24 text-right">Weight</Th>
                <Th className="w-28 text-right">Price</Th>
                <Th className="w-28 text-right">Change</Th>
                <Th className="w-24 text-right">% Chg</Th>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 18 }).map((_, i) => (
                  <SkeletonRow key={`skel-${i}`} />
                ))
              ) : filtered.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={7} className="py-16 text-center">
                    <p className="font-mono-tight uppercase tracking-[0.25em] text-muted-soft text-sm">
                      · No tickers match query ·
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((t) => (
                  <TickerRow
                    key={t.symbol}
                    ticker={t}
                    selected={t.symbol === selectedSymbol}
                    onSelect={onSelect}
                  />
                ))
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </section>
  );
}

function Th({ className = "", children }) {
  return (
    <TableHead
      className={`h-9 px-3 font-mono-tight text-[10px] text-muted-soft uppercase tracking-[0.2em] bg-surface ${className}`}
    >
      {children}
    </TableHead>
  );
}

function TickerRow({ ticker, selected, onSelect }) {
  const changeColor = pickTrendColor(ticker.change);
  const Icon =
    ticker.change > 0 ? TrendingUp : ticker.change < 0 ? TrendingDown : Minus;

  return (
    <TableRow
      data-testid={`ticker-row-${ticker.symbol}`}
      onClick={() => onSelect?.(ticker)}
      className={`cursor-pointer border-b border-[var(--grid)] group transition-colors ${
        selected
          ? "bg-surface-raised"
          : "hover:bg-surface-raised"
      }`}
    >
      <TableCell className="px-3 py-2 num text-right text-muted-soft text-xs">
        {ticker.rank ?? "—"}
      </TableCell>
      <TableCell className="px-3 py-2">
        <span
          className={`num font-bold text-sm ${
            selected ? "text-accent-amber" : "text-[var(--text-primary)] group-hover:text-accent-amber"
          } transition-colors`}
        >
          {ticker.symbol}
        </span>
      </TableCell>
      <TableCell className="px-3 py-2 text-sm text-[var(--text-primary)] truncate max-w-[240px]">
        {ticker.company || "—"}
      </TableCell>
      <TableCell className="px-3 py-2 num text-right text-sm text-muted-soft">
        {fmtWeight(ticker.weight)}
      </TableCell>
      <TableCell className="px-3 py-2 num text-right text-sm text-[var(--text-primary)]">
        {fmtPrice(ticker.price)}
      </TableCell>
      <TableCell className={`px-3 py-2 num text-right text-sm ${changeColor}`}>
        <span className="inline-flex items-center justify-end gap-1.5">
          <Icon size={12} strokeWidth={1.75} />
          {fmtSignedNum(ticker.change)}
        </span>
      </TableCell>
      <TableCell className={`px-3 py-2 num text-right text-sm ${changeColor}`}>
        {fmtPct(ticker.change_percent)}
      </TableCell>
    </TableRow>
  );
}

function SkeletonRow() {
  // Match the 7 columns of the real header: rank / symbol / company /
  // weight / price / change / change-pct. Using column names as keys
  // (instead of array indices) so React preserves cell identity even if
  // we re-order columns later.
  const cols = ["rank", "symbol", "company", "weight", "price", "change", "pct"];
  return (
    <TableRow className="border-b border-[var(--grid)] hover:bg-transparent">
      {cols.map((c) => (
        <TableCell key={c} className="px-3 py-2">
          <Skeleton className="h-4 w-full bg-surface-raised rounded-none" />
        </TableCell>
      ))}
    </TableRow>
  );
}

function ErrorBlock({ error }) {
  const attempts = error?.attempts || [];
  return (
    <div
      data-testid="ticker-error"
      className="m-4 border border-[var(--negative)] bg-negative-soft p-4 font-mono-tight"
    >
      <p className="text-[11px] uppercase tracking-[0.3em] text-negative">
        ▲ All providers failed
      </p>
      <p className="text-xs text-[var(--text-primary)] mt-2 break-all">
        {error?.message || String(error)}
      </p>
      {attempts.length > 0 && (
        <ul className="mt-3 space-y-1 text-[11px] text-muted-soft">
          {attempts.map((a, i) => (
            <li key={`${a.provider}-${a.status}-${i}`} className="flex gap-2">
              <span className="w-6 text-right num">{i + 1}.</span>
              <span className="text-[var(--text-primary)]">{a.provider}</span>
              <span className="uppercase tracking-widest">· {a.status}</span>
              {a.reason ? (
                <span className="truncate text-muted-soft">— {a.reason}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
